/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {BasketIcon} from '@salesforce/retail-react-app/app/components/icons'
import {EmptyCartIcon} from '../../../components/icons/index'
import {
    Box,
    Button,
    Container,
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import PropTypes from 'prop-types'
import React from 'react'
import {FormattedMessage} from 'react-intl'
import {useIntl} from 'react-intl'

import Link from '../../../components/link'
import {colors} from '../../../constants'

const EmptyCart = ({isRegistered, onClose, ...props}) => {
    const navigate = useNavigation()
    const {formatMessage} = useIntl()

    return (
        <Box
            data-testid="sf-cart-empty"
            flex="1"
            minWidth="100%"
            width="full"
            bgGradient={props.bgColor || colors.blueBgGradient}
        >
            <Container
                width={props.width || ['342px', '466px']}
                padding={['12px', 0]}
                margin={['100px auto 171px', '140px auto 343px']}
            >
                <Box align="center">
                    <EmptyCartIcon
                        width={{base: '50px', lg: '62.5px'}}
                        height={{base: '42px', lg: '52.5px'}}
                    />
                </Box>
                <Text
                    align="center"
                    fontSize={{base: '32px', lg: '56px'}}
                    fontFamily="Raleway"
                    fontWeight="400"
                    mt="48px"
                >
                    <FormattedMessage
                        defaultMessage="Your cart is empty"
                        id="empty_cart.description.empty_cart"
                    />
                </Text>

                <Stack spacing="48px" alignItems="center" mt="24px">
                    <Text
                        align="center"
                        fontSize={{base: '14px', lg: '16px'}}
                        fontFamily="Roboto"
                        fontWeight="300"
                        lineHeight={{base: '20px', lg: '24px'}}
                    >
                        {isRegistered ? (
                            <FormattedMessage
                                defaultMessage="It looks like you haven’t added any items to your cart yet. Start exploring our luxurious collection and add your favourite products to your cart."
                                id="empty_cart.message.continue_shopping"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Sign in to retrieve your saved items or continue shopping."
                                id="empty_cart.message.sign_in_or_continue_shopping"
                            />
                        )}
                    </Text>

                    <Flex
                        flexDirection={[
                            {base: 'column', md: 'column', lg: 'row'}
                        ]}
                        gap="10px"
                        width="100%"
                        justifyContent="center"
                    >
                        {/* TODO: This same button styling is being used at multiple places, we can create one for all later */}

                        <Link
                            buttonStyles={{
                                color: colors.black,
                                fontSize: '20px',
                                fontWeight: '300',
                                fontFamily: 'Roboto',
                                textDecoration: 'none',
                                _hover: {
                                    textDecoration: 'none'
                                },
                                _focus: {
                                    background: colors.transparent,
                                    backgroundColor: colors.transparent
                                },
                                width: '100%',
                                background: colors.transparent,
                                backgroundColor: colors.transparent
                            }}
                            type="tertiary"
                            text={
                                <FormattedMessage
                                    defaultMessage="Start Shopping"
                                    id="empty_cart.buttons.start_shopping"
                                />
                            }
                            onLinkClick={() => {
                                onClose && onClose()
                                navigate('/shop-all')
                            }}
                        />

                        {!isRegistered && (
                            <Link
                                buttonStyles={{
                                    color: colors.black,
                                    fontSize: '20px',
                                    fontWeight: '300',
                                    fontFamily: 'Roboto',
                                    textDecoration: 'none',
                                    _hover: {
                                        textDecoration: 'none'
                                    },
                                    _focus: {
                                        background: colors.transparent,
                                        backgroundColor: colors.transparent
                                    },
                                    width: '100%',
                                    background: colors.transparent,
                                    backgroundColor: colors.transparent
                                }}
                                type="tertiary"
                                text={
                                    <FormattedMessage
                                        defaultMessage="Sign In"
                                        id="empty_cart.buttons.sign_in"
                                    />
                                }
                                onLinkClick={() => {
                                    onClose && onClose()
                                    navigate('/login')
                                }}
                            />
                        )}
                    </Flex>
                </Stack>
            </Container>
        </Box>
    )
}
EmptyCart.propTypes = {isRegistered: PropTypes.bool, onClose: PropTypes.func}

export default EmptyCart
